import React from 'react';

/**
 * Shared "Subscription Required" state used everywhere an endpoint returns a
 * subscription/entitlement error. Keeps the paywall design consistent across
 * the app (Snipe Feed, Hot Props, Odds Screen, Prop Tool, Snipe Hub tables,
 * price movement panels, etc.).
 *
 * Sleek, static treatment that matches the app's slate/emerald surface language.
 *
 * Props:
 *  - feature:  what's gated, completes "…access to {feature}."
 *              e.g. "the Snipe Feed", "this player's price movements".
 *  - note:     optional secondary line (e.g. trial-tier caveat).
 *  - variant:  'page' (full-width centered) | 'panel' (modal/section) |
 *              'inline' (compact, for table cells and small containers).
 *  - flush:    drop the built-in outer centering/padding when the host already
 *              provides a centered wrapper (avoids double padding).
 *  - className: extra classes for the outer wrapper.
 */

const SIZES = {
    page: {
        outer: 'py-16 px-6',
        card: 'max-w-xs px-6 py-7',
        tile: 'w-10 h-10 rounded-xl',
        tileIcon: 'w-5 h-5',
        eyebrow: 'text-[9px] tracking-[0.22em] mt-4 mb-2',
        title: 'text-base mb-1.5',
        desc: 'text-[12px] max-w-[240px] mb-4',
        tierWrap: 'mb-5',
        tier: 'px-2 py-0.5 text-[9px]',
        note: 'text-[11px] mb-5',
        btn: 'px-4 py-2 text-xs',
        btnIcon: 'w-3.5 h-3.5',
    },
    panel: {
        outer: 'py-8 px-4',
        card: 'max-w-[280px] px-5 py-6',
        tile: 'w-9 h-9 rounded-lg',
        tileIcon: 'w-4 h-4',
        eyebrow: 'text-[9px] tracking-[0.2em] mt-3 mb-1.5',
        title: 'text-sm mb-1.5',
        desc: 'text-[11px] max-w-[240px] mb-3.5',
        tierWrap: 'mb-4',
        tier: 'px-2 py-0.5 text-[9px]',
        note: 'text-[10px] mb-4',
        btn: 'px-3.5 py-1.5 text-[11px]',
        btnIcon: 'w-3 h-3',
    },
    inline: {
        outer: 'py-6 px-3',
        card: 'max-w-[260px] px-4 py-5',
        tile: 'w-8 h-8 rounded-lg',
        tileIcon: 'w-4 h-4',
        eyebrow: 'text-[9px] tracking-[0.18em] mt-2.5 mb-1',
        title: 'text-[13px] mb-1',
        desc: 'text-[11px] max-w-[230px] mb-3',
        tierWrap: 'mb-3.5',
        tier: 'px-1.5 py-0.5 text-[9px]',
        note: 'text-[10px] mb-3.5',
        btn: 'px-3 py-1.5 text-[11px]',
        btnIcon: 'w-3 h-3',
    },
};

function LockIcon({ className }) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
    );
}

function TierPill({ label, tone, className }) {
    const tones = {
        emerald: 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300',
        teal: 'bg-teal-500/10 border-teal-500/25 text-teal-200',
    };
    return (
        <span className={`inline-flex items-center rounded-md border font-mono font-semibold uppercase tracking-wider ${tones[tone]} ${className}`}>
            {label}
        </span>
    );
}

function SubscriptionRequired({
    feature = 'this feature',
    note = null,
    variant = 'page',
    flush = false,
    className = '',
}) {
    const s = SIZES[variant] || SIZES.page;

    return (
        <div className={`flex items-center justify-center ${flush ? '' : s.outer} ${className}`}>
            <div
                className={`relative w-full ${s.card} rounded-2xl border border-slate-800/80 bg-slate-900/50 overflow-hidden flex flex-col items-center text-center`}
            >
                {/* Subtle emerald hairline along the top edge */}
                <div className="pointer-events-none absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent" />

                <div className={`${s.tile} bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-300`}>
                    <LockIcon className={s.tileIcon} />
                </div>

                <span className={`${s.eyebrow} font-mono uppercase text-emerald-400/80`}>
                    Premium Access
                </span>

                <h4 className={`${s.title} font-semibold font-mono tracking-tight text-white`}>
                    Subscription Required
                </h4>

                <p className={`${s.desc} font-mono text-slate-400 leading-relaxed`}>
                    Access to {feature} is reserved for Snipe members.
                </p>

                <div className={`flex items-center gap-1.5 ${s.tierWrap}`}>
                    <TierPill label="Snipe Pro" tone="emerald" className={s.tier} />
                    <TierPill label="Snipe Elite" tone="teal" className={s.tier} />
                </div>

                {note && (
                    <p className={`${s.note} font-mono text-slate-600 leading-relaxed max-w-[300px]`}>
                        {note}
                    </p>
                )}

                <a
                    href="/manage-subscriptions"
                    className={`${s.btn} inline-flex items-center gap-2 rounded-lg font-mono font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 transition-colors`}
                >
                    Manage Subscriptions
                    <svg className={s.btnIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                    </svg>
                </a>
            </div>
        </div>
    );
}

export default SubscriptionRequired;
